import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import { auth, db, initializeFirebase } from "./init";
import { executeWithRetry, ensureValidAuth, checkOnlineStatus } from "./utils";
import { UserData } from "./types";

export const createUser = async (userData: UserData): Promise<string | null> => {
  return executeWithRetry(async () => {
    try {
      await ensureValidAuth();
      await checkOnlineStatus();
      
      const { email, ...rest } = userData;
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, rest.referrerCode);
      const userId = userCredential.user.uid;
      
      const userDataToSave: Record<string, any> = {
        ...(rest || {}),
        email,
        createdAt: Timestamp.now(),
        isAdmin: email.toLowerCase() === "info@solarinphoenix.com" ? true : false
      };
      
      await setDoc(doc(db, "users", userId), userDataToSave);
      
      return userId;
    } catch (error) {
      console.error("Error creating user:", error);
      throw error;
    }
  });
};

export const loginUser = async (email: string, referrerCode: string): Promise<any> => {
  return executeWithRetry(async () => {
    try {
      await initializeFirebase();
      return await signInWithEmailAndPassword(auth, email, referrerCode);
    } catch (error) {
      console.error("Error logging in:", error);
      throw error;
    }
  });
};

export const logoutUser = async (): Promise<void> => {
  try {
    if (!auth) await initializeFirebase();
    await signOut(auth);
  } catch (error) {
    console.error("Error logging out:", error);
    throw error;
  }
};

export const getUserInfo = async (userId: string): Promise<UserData | null> => {
  return executeWithRetry(async () => {
    if (!userId) throw new Error("Invalid user ID");
    
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() } as UserData;
    }
    
    return null;
  });
};

import { auth } from './init';

export const ensureValidAuth = async (): Promise<void> => {
  if (!auth?.currentUser) {
    throw new Error("Not authenticated");
  }
  
  try {
    await auth.currentUser.getIdToken(true);
  } catch (e) {
    console.log("Token refresh failed, logging out");
    await auth.signOut();
    throw new Error("Your session has expired. Please log in again.");
  }
};
